import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { ServiceChapters } from "@/components/ServiceChapters";
import { Offerings } from "@/components/Offerings";
import { Coverage } from "@/components/Coverage";
import { About } from "@/components/About";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import AdminPage from "@/pages/AdminPage";
import { API } from "@/lib/scroll";

const HomePage = () => (
  <>
    <Navbar />
    <main>
      <Hero />
      <Marquee />
      <ServiceChapters />
      <Offerings />
      <Coverage />
      <About />
      <Contact />
    </main>
    <Footer />
  </>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    axios
      .post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then((r) => navigate("/admin", { replace: true, state: { user: r.data } }))
      .catch((e) => {
        const d = e.response?.data?.detail;
        navigate("/admin", {
          replace: true,
          state: { error: typeof d === "string" ? d : "Google sign-in failed" },
        });
      });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-ark-950 flex flex-col items-center justify-center gap-4">
      <Loader2 size={32} className="animate-spin text-cyan-400" />
      <p className="font-mono text-xs tracking-[0.25em] text-slate-500 uppercase">
        Completing sign-in…
      </p>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/admin" element={<AdminPage />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <div className="noise-overlay" aria-hidden="true" />
      <Toaster theme="dark" position="bottom-right" richColors />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
